import { useState, useEffect, useCallback } from 'react';
import { rfqRepository } from '../repositories/rfq.repository';
import { shouldMakeAPICall, onNetworkStatusChange } from '../utils/network';

/**
 * Hook for accessing offline RFQ list.
 * When skipSyncWhenOnline, we never sync from API (RFQ list uses server-side pagination + background sync).
 */
export const useOfflineRFQs = (
  authToken,
  orgToken,
  pageNumber = 1,
  searchQuery = '',
  startDate = '',
  endDate = '',
  sortingColumn = '',
  sortingOrder = '',
  skipSyncWhenOnline = false
) => {
  const [rfqs, setRFQs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRFQs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const cachedRFQs = await rfqRepository.getRFQsFromIndexedDB(
        orgToken,
        pageNumber,
        searchQuery,
        startDate,
        endDate,
        sortingColumn,
        sortingOrder
      );
      const optimisticCount = cachedRFQs.filter(rfq => rfq._pendingSync || rfq._lastUpdated).length;
      console.log(`📦 Loaded ${cachedRFQs.length} RFQs from IndexedDB${pageNumber ? ` (page ${pageNumber})` : ''} (${optimisticCount} with optimistic updates)`);
      
      setRFQs(cachedRFQs);

      const online = shouldMakeAPICall(authToken, orgToken) && authToken && orgToken;
      if (online && !skipSyncWhenOnline) {
        console.log('🌐 Online - syncing RFQs from API');
        const syncedRFQs = await rfqRepository.syncFromAPI(
          authToken,
          orgToken,
          pageNumber,
          searchQuery,
          startDate,
          endDate,
          sortingColumn,
          sortingOrder
        );
        if (syncedRFQs && syncedRFQs.length >= 0) {
          const syncedOptimistic = syncedRFQs.filter(rfq => rfq._pendingSync || rfq._lastUpdated).length;
          console.log(`✅ Synced ${syncedRFQs.length} RFQs (${syncedOptimistic} optimistic updates preserved)`);
          setRFQs(syncedRFQs);
        }
      } else if (!online) {
        console.log('📴 Offline - using cached RFQ data only');
      } else if (skipSyncWhenOnline) {
        console.log('🌐 Online - skip sync (server-side pagination + background sync)');
      }
    } catch (err) {
      console.error('Error loading RFQs:', err);
      setError(err);
      const cachedRFQs = await rfqRepository.getAllFromCache(orgToken);
      setRFQs(cachedRFQs);
    } finally {
      setLoading(false);
    }
  }, [authToken, orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder, skipSyncWhenOnline]);

  useEffect(() => {
    loadRFQs();

    // Listen for network changes
    const cleanup = onNetworkStatusChange(
      () => {
        if (shouldMakeAPICall(authToken, orgToken) && authToken && orgToken) {
          loadRFQs();
        }
      },
      () => {
        console.log('📴 Offline mode - using cached RFQ data');
      }
    );

    return cleanup;
  }, [loadRFQs, authToken, orgToken]);

  return { rfqs, loading, error, refetch: loadRFQs };
};

/**
 * Hook for accessing RFQ details
 */
export const useRFQDetails = (authToken, rfqToken, buyerToken = null) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDetails = useCallback(async () => {
    if (!rfqToken) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load from cache first
      const cachedDetails = await rfqRepository.getRFQDetailsFromCache(rfqToken);
      if (cachedDetails) {
        console.log(`📦 Loaded RFQ details from cache for ${rfqToken}`);
        setDetails(cachedDetails);
      }

      // Sync if online
      if (shouldMakeAPICall(authToken, buyerToken) && authToken && buyerToken) {

        console.log('🌐 Online - syncing RFQ details from API');
        const syncedDetails = await rfqRepository.syncRFQDetailsFromAPI(authToken, rfqToken, buyerToken);
        if (syncedDetails) {
          setDetails(syncedDetails);
        } else if (cachedDetails) {
          setDetails(cachedDetails);
        }
      } else if (cachedDetails) {
        setDetails(cachedDetails);
      }
    } catch (err) {
      console.error('Error loading RFQ details:', err);
      setError(err);
      const cachedDetails = await rfqRepository.getRFQDetailsFromCache(rfqToken);
      if (cachedDetails) {
        setDetails(cachedDetails);
      }
    } finally {
      setLoading(false);
    }
  }, [authToken, rfqToken, buyerToken]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  return { details, loading, error, refetch: loadDetails };
};

/**
 * Hook for accessing RFQ bids
 */
export const useRFQBids = (authToken,buyerToken, rfqToken, quoteNo = null) => {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBids = useCallback(async () => {
    if (!rfqToken) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load from cache first
      const cachedBids = await rfqRepository.getBidsFromCache(rfqToken);
      if (cachedBids && cachedBids.length > 0) {
        console.log(`📦 Loaded ${cachedBids.length} bids from cache for RFQ ${rfqToken}`);
        setBids(cachedBids);
      }

      // Sync if online
      if (shouldMakeAPICall(authToken, buyerToken) && authToken && buyerToken) {

        console.log('🌐 Online - syncing bids from API');
        const syncedBids = await rfqRepository.syncBidsFromAPI(authToken, rfqToken, quoteNo);
        if (syncedBids) {
          setBids(Array.isArray(syncedBids) ? syncedBids : []);
        } else if (cachedBids && cachedBids.length > 0) {
          setBids(cachedBids);
        }
      } else if (cachedBids && cachedBids.length > 0) {
        setBids(cachedBids);
      }
    } catch (err) {
      console.error('Error loading bids:', err);
      setError(err);
      const cachedBids = await rfqRepository.getBidsFromCache(rfqToken);
      if (cachedBids) {
        setBids(cachedBids);
      }
    } finally {
      setLoading(false);
    }
  }, [authToken, rfqToken, quoteNo]);

  useEffect(() => {
    loadBids();
  }, [loadBids]);

  return { bids, loading, error, refetch: loadBids };
};

/**
 * Hook for accessing RFQ messages
 */
export const useRFQMessages = (authToken,buyerToken, rfqToken, pageNumber = 1) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMessages = useCallback(async () => {
    if (!rfqToken) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load from cache first
      const cachedMessages = await rfqRepository.getMessagesFromCache(rfqToken);
      if (cachedMessages && cachedMessages.length > 0) {
        console.log(`📦 Loaded ${cachedMessages.length} messages from cache for RFQ ${rfqToken}`);
        setMessages(cachedMessages);
      }

      // Sync if online
     if (shouldMakeAPICall(authToken, buyerToken) && authToken && buyerToken) {

        console.log('🌐 Online - syncing messages from API');
        const syncedMessages = await rfqRepository.syncMessagesFromAPI(authToken, rfqToken, pageNumber);
        if (syncedMessages) {
          setMessages(Array.isArray(syncedMessages) ? syncedMessages : []);
        } else if (cachedMessages && cachedMessages.length > 0) {
          setMessages(cachedMessages);
        }
      } else if (cachedMessages && cachedMessages.length > 0) {
        setMessages(cachedMessages);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      setError(err);
      const cachedMessages = await rfqRepository.getMessagesFromCache(rfqToken);
      if (cachedMessages) {
        setMessages(cachedMessages);
      }
    } finally {
      setLoading(false);
    }
  }, [authToken, rfqToken, pageNumber]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  return { messages, loading, error, refetch: loadMessages };
};

/**
 * Hook for accessing RFQ chat timeline
 */
export const useRFQChatTimeline = (authToken, rfqToken, buyerToken, vendorToken = null) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTimeline = useCallback(async () => {
    if (!rfqToken || !buyerToken) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load from cache first (keyed by buyer_token, rfq_token, vendor_token)
      const cachedTimeline = await rfqRepository.getChatTimelineFromCache(rfqToken, vendorToken, buyerToken);
      if (cachedTimeline && cachedTimeline.length > 0) {
        console.log(`📦 Loaded chat timeline from cache for RFQ ${rfqToken}`);
        setTimeline(cachedTimeline);
      }

      // Sync if online
      if (shouldMakeAPICall(authToken, buyerToken) && authToken && buyerToken) {
        console.log('🌐 Online - syncing chat timeline from API');
        const syncedTimeline = await rfqRepository.syncChatTimelineFromAPI(
          authToken,
          rfqToken,
          buyerToken,
          vendorToken
        );
        if (syncedTimeline) {
          setTimeline(Array.isArray(syncedTimeline) ? syncedTimeline : []);
        } else if (cachedTimeline && cachedTimeline.length > 0) {
          setTimeline(cachedTimeline);
        }
      } else if (cachedTimeline && cachedTimeline.length > 0) {
        setTimeline(cachedTimeline);
      }
    } catch (err) {
      console.error('Error loading chat timeline:', err);
      setError(err);
      const cachedTimeline = await rfqRepository.getChatTimelineFromCache(rfqToken, vendorToken, buyerToken);
      if (cachedTimeline) {
        setTimeline(cachedTimeline);
      }
    } finally {
      setLoading(false);
    }
  }, [authToken, rfqToken, buyerToken, vendorToken]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  return { timeline, loading, error, refetch: loadTimeline };
};
